import { useState, useEffect } from "react";
import Footer from "./Footer";
import config from "../config";

const { API_BASE, UPLOADS_URL } = config;

async function fetchJSON(url) {
  const res = await fetch(url);
  if (!res.ok) throw new Error(`HTTP ${res.status} – ${url}`);
  return res.json();
}

function formatDate(dateStr) {
  if (!dateStr) return "";
  const d = new Date(dateStr);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" });
}

function Breadcrumb() {
  return (
    <nav className="bl-bread" aria-label="Breadcrumb">
      <a href="/" className="bl-bread__link">Home</a>
      <span className="bl-bread__sep" aria-hidden="true">›</span>
      <span className="bl-bread__current">Blog</span>
    </nav>
  );
}

function BlogCard({ post }) {
  return (
    <a href={`/blog/${post.slug}`} className="bl-card">
      <div className="bl-card__img-wrap">
        {post.image1 ? (
          <img
            src={`${UPLOADS_URL}/blogs/${post.image1}`}
            alt={post.title}
            className="bl-card__img"
          />
        ) : (
          <div className="bl-card__img bl-card__img--placeholder" />
        )}
        {post.category && <span className="bl-card__category">{post.category}</span>}
      </div>
      <div className="bl-card__body">
        <div className="bl-card__meta">
          {post.author && <span className="bl-card__author">By {post.author}</span>}
          {post.published_at && (
            <>
              <span className="bl-card__dot" aria-hidden="true">•</span>
              <span className="bl-card__date">{formatDate(post.published_at)}</span>
            </>
          )}
        </div>
        <h3 className="bl-card__title">{post.title}</h3>
        {post.excerpt && <p className="bl-card__excerpt">{post.excerpt}</p>}
        <span className="bl-card__readmore">
          Read More
          <svg width="13" height="13" viewBox="0 0 14 14" fill="none" aria-hidden="true">
            <path d="M2 7h10M8 3l4 4-4 4" stroke="currentColor" strokeWidth="1.8"
              strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </span>
      </div>
    </a>
  );
}

function CategoryFilter({ categories, active, onSelect }) {
  if (!categories.length) return null;
  return (
    <div className="bl-filters">
      <button
        className={`bl-filters__btn ${active === null ? "bl-filters__btn--active" : ""}`}
        onClick={() => onSelect(null)}
      >
        All
      </button>
      {categories.map((cat) => (
        <button
          key={cat}
          className={`bl-filters__btn ${active === cat ? "bl-filters__btn--active" : ""}`}
          onClick={() => onSelect(cat)}
        >
          {cat}
        </button>
      ))}
    </div>
  );
}

function BlogPage() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeCategory, setActiveCategory] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);

    fetchJSON(`${API_BASE}/blogs.php`)
      .then((data) => {
        if (cancelled) return;
        const list = Array.isArray(data) ? data.filter((p) => p.status !== "draft") : [];
        setPosts(list);
        setLoading(false);
      })
      .catch((err) => {
        if (cancelled) return;
        console.error("BlogPage load error:", err);
        setError("Failed to load blog posts.");
        setLoading(false);
      });

    return () => { cancelled = true; };
  }, []);

  if (loading) return null;

  if (error) return (
    <div className="bl-error">
      <h2>Something went wrong</h2>
      <p>We could not load the blog. Please try again later.</p>
      <a href="/" className="bl-error__link">Back to Home</a>
    </div>
  );

  const categories = [...new Set(posts.map((p) => p.category).filter(Boolean))];
  const filteredPosts = activeCategory
    ? posts.filter((p) => p.category === activeCategory)
    : posts;

  return (
    <>
      <div className="bl">

        {/* ── HERO ── */}
        <div className="bl-hero">
          <div className="bl-hero__overlay" />
          <div className="bl-hero__content">
            <Breadcrumb />
            <h1 className="bl-hero__title">Our Blog</h1>
          </div>
        </div>

        {/* ── BODY ── */}
        <div className="bl-body">
          <div className="bl-body__inner">

            <CategoryFilter
              categories={categories}
              active={activeCategory}
              onSelect={setActiveCategory}
            />

            {filteredPosts.length > 0 ? (
              <div className="bl-grid">
                {filteredPosts.map((post) => (
                  <BlogCard key={post.id} post={post} />
                ))}
              </div>
            ) : (
              <div className="bl-empty">No blog posts found.</div>
            )}

          </div>
        </div>

        <style>{`
          @import url('https://fonts.googleapis.com/css2?family=Roboto:wght@400;500;600;700;800;900&display=swap');

          .bl *, .bl *::before, .bl *::after { box-sizing: border-box; margin: 0; padding: 0; }
          .bl a { text-decoration: none; }
          .bl ul { list-style: none; }
          .bl { font-family: 'Roboto', sans-serif; background: #f5f7fa; }

          .bl-error { text-align: center; padding: 80px 24px; font-family: 'Roboto', sans-serif; color: #424244; }
          .bl-error h2 { font-size: 28px; color: #013d97; margin-bottom: 12px; }
          .bl-error p  { margin-bottom: 24px; }
          .bl-error__link { display: inline-block; padding: 10px 24px; background: #029add; color: #fff; border-radius: 6px; font-weight: 700; }

          .bl-hero { position: relative; height: 220px; overflow: hidden; background: #013d97; }
          .bl-hero__overlay { position: absolute; inset: 0; background: linear-gradient(135deg, #013d97, #029add); }
          .bl-hero__content { position: relative; height: 100%; display: flex; flex-direction: column; justify-content: flex-end; padding: 32px 40px; max-width: 1280px; margin: 0 auto; }
          .bl-hero__title { font-size: clamp(28px, 3.5vw, 44px); font-weight: 900; color: #fff; line-height: 1.1; margin-top: 10px; }

          .bl-bread { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; }
          .bl-bread__link { font-size: 13px; font-weight: 600; color: rgba(255,255,255,.85); }
          .bl-bread__link:hover { color: #fff; }
          .bl-bread__sep { color: rgba(255,255,255,.5); font-size: 14px; }
          .bl-bread__current { font-size: 13px; color: rgba(255,255,255,.7); }

          .bl-body { padding: 56px 0 80px; }
          .bl-body__inner { max-width: 1280px; margin: 0 auto; padding: 0 40px; }

          .bl-filters { display: flex; gap: 10px; flex-wrap: wrap; margin-bottom: 36px; }
          .bl-filters__btn {
            padding: 9px 20px; border-radius: 20px; border: 1px solid #d8dee9;
            background: #fff; color: #5a5f72; font-size: 13px; font-weight: 700;
            cursor: pointer; transition: all .2s;
          }
          .bl-filters__btn:hover { border-color: #029add; color: #029add; }
          .bl-filters__btn--active { background: #013d97; border-color: #013d97; color: #fff; }

          .bl-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 28px; }

          .bl-empty { text-align: center; padding: 60px 0; color: #8890a0; font-size: 14px; }

          .bl-card {
            display: flex; flex-direction: column; background: #fff; border-radius: 6px;
            overflow: hidden; box-shadow: 0 2px 14px rgba(0,0,0,.06);
            transition: transform .25s, box-shadow .25s;
          }
          .bl-card:hover { transform: translateY(-4px); box-shadow: 0 10px 28px rgba(0,0,0,.12); }

          .bl-card__img-wrap { position: relative; aspect-ratio: 16/10; overflow: hidden; background: #e6f0fb; }
          .bl-card__img { width: 100%; height: 100%; object-fit: cover; display: block; transition: transform .35s; }
          .bl-card:hover .bl-card__img { transform: scale(1.06); }
          .bl-card__img--placeholder { background: linear-gradient(135deg, #013d97, #029add); }
          .bl-card__category {
            position: absolute; top: 14px; left: 14px;
            background: #e96c29; color: #fff; font-size: 11px; font-weight: 800;
            letter-spacing: .05em; text-transform: uppercase;
            padding: 5px 12px; border-radius: 3px;
          }

          .bl-card__body { display: flex; flex-direction: column; gap: 10px; padding: 22px; }
          .bl-card__meta { display: flex; align-items: center; gap: 8px; font-size: 12px; color: #8890a0; font-weight: 600; }
          .bl-card__dot { color: #c5cad6; }
          .bl-card__title { font-size: 17px; font-weight: 800; color: #1a1a2e; line-height: 1.35; }
          .bl-card__excerpt { font-size: 13.5px; color: #6b7280; line-height: 1.65; display: -webkit-box; -webkit-line-clamp: 3; -webkit-box-orient: vertical; overflow: hidden; }
          .bl-card__readmore { display: inline-flex; align-items: center; gap: 6px; font-size: 12.5px; font-weight: 800; color: #029add; letter-spacing: .03em; margin-top: 4px; }
          .bl-card:hover .bl-card__readmore { color: #013d97; }

          @media (max-width: 1024px) {
            .bl-grid { grid-template-columns: repeat(2, 1fr); }
          }
          @media (max-width: 700px) {
            .bl-grid { grid-template-columns: 1fr; }
            .bl-body__inner { padding: 0 24px; }
            .bl-hero__content { padding: 20px 20px; }
          }

          a:focus-visible, button:focus-visible { outline: 2px solid #029add; outline-offset: 2px; }
          @media (prefers-reduced-motion: reduce) { .bl *, .bl *::before, .bl *::after { transition-duration: 0ms !important; } }
        `}</style>
      </div>
      <Footer />
    </>
  );
}

export default BlogPage;